<template>
  <section style="width: 1200px">
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card" style="width: 1200px">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ title }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group
              horizontal
              name="status"
              attribute="attributes.status"
              :label="$t('attributes.status')"
            >
              <b-switch v-model="form.status" name="status"></b-switch>
            </form-group>

            <form-group horizontal name="topic" :label="$t('attributes.topic')">
              <b-input
                v-model="form.topic"
                name="topic"
                :placeholder="
                  $t('message.please_input') + $t('attributes.topic')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="type"
              :label="$t('attributes.news_type')"
            >
              <b-select v-model="form.type" name="type" expanded>
                <option value="A">
                  {{ $t('attributes.news_link_type') }}
                </option>
                <option value="B">
                  {{ $t('attributes.news_normal_type') }}
                </option>
                <option value="B1">
                  {{ $t('attributes.news_blog_type') }}
                </option>
              </b-select>
            </form-group>

            <form-group
              horizontal
              name="catalog"
              :label="$t('attributes.news_catalog')"
            >
              <b-select v-model="form.catalog" name="catalog" expanded>
                <option value="近期活動">近期活動</option>
                <option value="活動回顧">活動回顧</option>
              </b-select>
            </form-group>

            <form-group
              horizontal
              name="signup_dates"
              attribute="attributes.date_range"
              :label="$t('attributes.news_dates')"
            >
              <b-datepicker
                :placeholder="
                  $t('message.please_select') + $t('attributes.news_dates')
                "
                v-model="selectedDates"
                range
                @input="onSelectedDates"
              >
              </b-datepicker>
            </form-group>

            <form-group horizontal name="url" :label="$t('attributes.url')">
              <b-input
                v-model="form.url"
                name="url"
                :placeholder="$t('message.please_input') + $t('attributes.url')"
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="picture"
              attribute="attributes.picture"
              :label="$t('attributes.picture')"
              grouped
            >
              <template #label>
                {{ $t('attributes.picture') }}
                <b-tooltip type="is-dark" label="400*240/限定jpg檔，100KB內">
                  <b-icon
                    size="is-small"
                    type="is-danger"
                    icon="help-circle-outline"
                  ></b-icon>
                </b-tooltip>
              </template>
              <b-field class="file">
                <a class="button is-primary" @click="onSelectPictureImage">
                  <b-icon icon="upload"></b-icon>
                  <span>{{ $t('message.please_select_file') }}</span>
                </a>
                <figure v-if="form.picture" class="picture">
                  <a
                    :href="NewsImage(form.picture)"
                    @click.prevent="onClickImage(form.picture)"
                  >
                    <img
                      :src="NewsImage(form.picture)"
                      :alt="$t('attributes.picture')"
                      style="height: 40px; width: auto; margin-left: 6px;"
                    />
                  </a>
                </figure>
              </b-field>
            </form-group>

            <form-group
              horizontal
              name="description"
              :label="$t('attributes.description')"
            >
              <b-input
                v-model="form.description"
                name="description"
                type="textarea"
                :placeholder="
                  $t('message.please_input') + $t('attributes.description')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="content"
              :label="$t('attributes.content')"
            >
              <div class="content">
                <editor
                  ref="content_editor"
                  v-model="form.content"
                  image-type="News"
                ></editor>
              </div>
            </form-group>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button
            :loading="sending"
            class="is-fullwidth"
            @click="$parent.close()"
          >
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="sending"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ isUpdate ? $t('attributes.update') : $t('attributes.add') }}
          </b-button>
        </footer>

        <b-modal :active.sync="isImageModalActive">
          <p class="image">
            <img :src="NewsImage(selectedImageUrl)" alt="" />
          </p>
        </b-modal>

        <b-loading
          :active.sync="isLoading"
          :is-full-page="false"
          :can-cancel="false"
        ></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'
import { OpenNewsFinder } from '@/utils/ckfinder'
import notifyService from '@/services/notifyService'
import Editor from '@/components/editors/Editor4'

export default {
  name: 'NewsForm',
  components: { Editor },

  props: {
    news: {
      type: Object,
      required: true
    }
  },
  data() {
    const isUpdate = !!this.news.id
    const title = isUpdate
      ? this.$t('attributes.modify')
      : this.$t('attributes.add')

    const today = this.$dayjs()
    const startDate = this.news.start_date
      ? this.$dayjs(this.news.start_date).toDate()
      : today.toDate()
    const endDate = this.news.end_date
      ? this.$dayjs(this.news.end_date).toDate()
      : today.toDate()

    return {
      isUpdate: isUpdate,
      title: title,

      selectedDates: [startDate, endDate],

      sending: false,
      form: this.extractFormValues(this.news),

      isImageModalActive: false,
      selectedImageUrl: null
    }
  },
  validations: {
    form: {
      status: { required },
      type: { required },
      catalog: { required },
      topic: { required, maxLength: maxLength(100) },
      description: { maxLength: maxLength(500) },
      content: {
        requiredIf: requiredIf(function(model) {
          return model.type !== 'A'
        })
      },
      url: {
        requiredIf: requiredIf(function(model) {
          return model.type === 'A'
        })
      },
      picture: { required, maxLength: maxLength(200) },
      start_date: { required },
      end_date: { required }
    }
  },
  computed: {
    isLoading() {
      return this.sending
    }
  },
  methods: {
    extractFormValues(data) {
      const today = this.$dayjs()
      const startDate = data.start_date
        ? this.$dayjs(data.start_date).toDate()
        : today.toDate()
      const endDate = data.end_date
        ? this.$dayjs(data.end_date).toDate()
        : today.toDate()

      this.selectedDates = [startDate, endDate]

      return {
        id: data.id,
        status: data.status,
        type: data.type,
        catalog: data.catalog,
        topic: data.topic,
        description: data.description,
        content: data.content,
        url: data.url,
        picture: data.picture,
        start_date: this.formatDate(startDate),
        end_date: this.formatDate(endDate)
      }
    },
    async onSelectPictureImage() {
      try {
        this.form.picture = await OpenNewsFinder()
      } catch (e) {
        notifyService.error(e)
      }
    },
    onSelectedDates() {
      this.form.start_date = this.formatDate(this.selectedDates[0])
      this.form.end_date = this.formatDate(this.selectedDates[1])
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = url
    },
    async onSend() {
      this.form.content = this.$refs.content_editor.getData()

      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
